import BaseUISystem from "../BaseUISystem";
import Point from "Utility/Point";
import { PopupID } from "State/UI/PopupID";
import { FarmCatalogueMenuLayout } from "Layouts/FarmCatalogueMenuLayout";
import RelativePositionComponent from "Component/Render/RelativePosition";
import BaseMenu from "System/UI/Menu/BaseMenu";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Interactable from "Component/Interactable";
import { FarmSlotId } from "State/Player/Farm/IFarmPlayerState";
import Container from "Prototype/Container";
import Sprite from "Prototype/Sprite";
import TransformComponent from "Component/Transform";
import { getFarmItemThumbnailAssetName, getFarmItemThumbnailPath, parseFarmSlotId } from "System/Farm/FarmHelpers";
import SpriteComponent from "Component/Render/Sprite";
import Game from "Game";
import { FarmSlot } from "Prototype/Farm/FarmSlot";
import { applyDefaultButtonAnimation } from "System/UI/UIHelper";
import Renderable from "Component/Renderable";
import FarmMapHandler from "System/Farm/Handler/FarmMapHandler";
import { IGameState } from "State/Game/IGameState";
import PIXIRenderer from "System/Render/PIXIRenderer";
import { FarmActions } from "State/Farm/FarmActions";
import { FarmSlotType } from "System/Farm/Data/FarmSlotType";
import { PlaySound } from "System/Audio/AudioHelper";
import { ScrollBox } from "Prototype/ScrollBox";
import { TickerEvent } from "Utility/Ticker";
import { ScrollBoxEvent } from "Prototype/UI/PIXIScrollBox";
import { FarmStateHelper } from "State/Player/Farm/FarmStateHelper";
import { Anim, StopAnimation } from "System/Animation/AnimationHelper";
import { RenderSystemEvent } from "System/Render/RenderSystem";
import { FTUXStateHelper } from "State/Game/FTUX/FTUXStateHelper";
import { FTUXType } from "Player/PlayerProperty/Progress/FTUXType";
import ColorFilter from "Component/ColorFilter";
import { TextAlignment } from "Prototype/UI/TextAlignment";
import Text from "Prototype/Text";
import { FarmItemId, getFarmItemByID } from "System/Farm/Data/FarmItemData";
import TextComponent from "Component/Render/Text";
import { createSelector, OutputSelector } from "reselect";

export enum FarmCatalogueTab {
    FLOWERS = 'myfarm_shop_flower',
    TREES = 'myfarm_shop_tree',
    HAMMERS = 'myfarm_shop_hammer',
    WHEELBARROWS = 'myfarm_shop_wheelbarrow',
    FENCES = 'myfarm_shop_fence',
    CROWNS = 'myfarm_shop_premium',
    HOUSE = 'reply_task_house',
}

const FarmCatalogueTabOrder: Record<FarmCatalogueTab, number> = {
    [FarmCatalogueTab.FLOWERS]: 0,
    [FarmCatalogueTab.TREES]: 1,
    [FarmCatalogueTab.FENCES]: 2,
    [FarmCatalogueTab.HAMMERS]: 3,
    [FarmCatalogueTab.WHEELBARROWS]: 4,
    [FarmCatalogueTab.HOUSE]: 5,
    [FarmCatalogueTab.CROWNS]: 6,
};

interface OnShowParams {
    slotId: FarmSlotId;
}

interface IEntry {
    root: Container;
    bg: Sprite;
    thumbnail: Sprite;
    lockedText: Text;
    lockedTextBG: Sprite;

    // Used to prevent selection while assets are loading.
    isLoading: boolean;

    // if addX is true
    xSprite?: Sprite;

    // if addTick is true
    tickSprite?: Sprite;
    glowSprite?: Sprite;

    itemId: FarmItemId;

    /**
     * This value is stored with each entry to determine if it has changed and should update its properties/visuals.
     */
    calculatedIndex: number;
}

const SPACE_BETWEEN_TABS: number = 75; // pixels
const SCROLL_ENTRY_X_OFFSET: number = 20;
const SCROLL_ENTRY_Y_OFFSET: number = 13;
const SCROLL_ENTRY_WIDTH_HEIGHT: number = 150;
const SCROLL_ENTRY_TOTAL_COL: number = 3;
const SCROLL_ENTRY_TOTAL_ROW: number = 4;
export default class FarmCatalogueMenuHandler extends BaseMenu {
    private activeSlot: FarmSlot = null;
    private highlightSprite: GameObject<PIXI.Sprite>;
    private isActiveSlotRemovable: boolean;

    private currentlySelectedItemID: FarmItemId = null;
    private currentTab: number = 0;
    private currentlyAvailableTabs: FarmCatalogueTab[] = [];
    private itemsAvailableInTab: FarmItemId[] = [];
    private tabIcons: GameObject[] = null;

    private layout: any;
    private _tabStartingPositionX: number;

    private scroller: ScrollBox;

    private entries: IEntry[] = [];

    private leftArrow: GameObject;
    private rightArrow: GameObject;

    private activeTabSelector: OutputSelector<IGameState, void, (res: FarmCatalogueTab) => void>;

    constructor(props: { system: BaseUISystem }) {
        super(PopupID.FARM_CATALOGUE, props.system, false);
    }

    public onCreate() {
        super.onCreate();

        this.layout = new FarmCatalogueMenuLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);
        this._tabStartingPositionX = this.layout.activeTab.container.x;

        this.leftArrow = this.layout.leftArrow;
        this.addEventListener(this.leftArrow, GameObjectEvent.SELECTED, () => this.activeSlot?.selectNextSlot('left'));
        this.rightArrow = this.layout.rightArrow;
        this.addEventListener(this.rightArrow, GameObjectEvent.SELECTED, () =>
            this.activeSlot?.selectNextSlot('right')
        );

        this.highlightSprite = GameObject.Instantiate(Sprite, {
            texture: 'farm_highlight',
            blendMode: PIXI.BLEND_MODES.SCREEN,
            alpha: 0.15,
            scale: { x: 1, y: 1 },
            anchor: { x: 0.5, y: 0.4 },
        });
        this.system.overlayPopup.parent.addChild(this.highlightSprite);

        this.activeTabSelector = createSelector(
            (state: IGameState) => state.farmState.activeTab,
            activeTab => {
                this.changeTab(this.currentlyAvailableTabs.indexOf(activeTab));
            }
        );
    }

    private createScrollBox(boxWidth: number, boxHeight: number, scrollWidth: number, scrollHeight: number) {
        if (this.scroller) {
            this.scroller.destroy();
        }

        this.entries = [];

        this.scroller = GameObject.Instantiate(ScrollBox, {
            name: 'scroller',
            overflowY: 'auto',
            dragScroll: true,
            boxWidth,
            boxHeight,
            scrollWidth,
            scrollHeight,
            noTicker: true,
            stopPropagation: false,
            verticalHandle: this.layout.scrollButton.container,
            scaleVerticalHandle: false,
        });

        this.scroller.resize(scrollWidth, scrollHeight);
        this.scroller.container.on(ScrollBoxEvent.MOVED, this.onScroll);

        this.layout.scrollContainer.addChild(this.scroller);

        for (let row = 0; row < SCROLL_ENTRY_TOTAL_ROW; row++) {
            for (let col = 0; col < SCROLL_ENTRY_TOTAL_COL; col++) {
                const baseIndex = col + row * SCROLL_ENTRY_TOTAL_COL;
                const entry = this.createEntry(baseIndex);
                this.scroller.container.content.addChild(entry.root.container);
                this.entries.push(entry);
            }
        }
    }

    private readonly onScroll = () => {
        this.updateEntries();
    };

    // TODO: This could be a layout
    private createEntry(baseIndex: number): IEntry {
        const root: Container = GameObject.Instantiate(Container, {
            x: 0,
            y: 0,
        });

        const bg: Sprite = GameObject.Instantiate(Sprite, {
            texture: 'myfarm_shop_ui_shadow',
            x: 138 / 2,
            y: 138 / 2,
            anchor: {
                x: 0.5,
                y: 0.5,
            },
        });
        root.addChild(bg);
        bg.scene = this.system.scene;
        bg.addComponent(Interactable, {
            enabled: true,
            buttonMode: true,
        });
        bg.on(GameObjectEvent.SELECTED, () => {
            this.onSelectItem(baseIndex);
        });
        applyDefaultButtonAnimation(bg);

        const thumbnail = GameObject.Instantiate(Sprite, {
            texture: 'loading_farm_icon',
            anchor: {
                x: 0.5,
                y: 0.5,
            },
            x: bg.container.width / 2 - 136 / 2,
            y: bg.container.height / 2 - 136 / 2,
        });

        thumbnail.addComponent(ColorFilter, {
            saturation: 1,
        });

        bg.addChild(thumbnail);

        const lockedTextBG = GameObject.Instantiate(Sprite, {
            texture: 'myfarm_shop_ui_lock',
            y: 136 / 2 - 20,
            anchor: {
                x: 0.52,
                y: 0.5,
            },
        });

        const lockedText = GameObject.Instantiate(Text, {
            name: 'text',
            text: '',
            style: {
                fontFamily: 'FrancoisOne',
                fontSize: 24,
                fill: '#fcffd9',
                align: TextAlignment.CENTER,
                strokeThickness: 4,
                stroke: '#811500',
                lineJoin: 'miter',
                miterLimit: 2,
            },
            anchor: new Point(0.5, 0.5),
            bounds: new Point(80, 45),
            x: 10,
        });

        lockedTextBG.addChild(lockedText);
        thumbnail.addChild(lockedTextBG);

        return {
            root,
            bg,
            thumbnail,
            lockedText,
            lockedTextBG,
            itemId: null,
            calculatedIndex: -1,
            isLoading: false,
        };
    }

    private onSelectItem(baseIndex: number): void {
        PlaySound('change_slot_item_any');

        const entry = this.entries[baseIndex];
        const prevEntryIndex = this.entries.findIndex(entry => entry.itemId === this.currentlySelectedItemID);
        this.currentlySelectedItemID = entry.itemId;

        this.updateEntrySelectionVisuals(baseIndex);

        // `prevEntryIndex` may be -1 if selection is `null` and the entry with `itemId == null` is not shown.
        if (prevEntryIndex !== -1) {
            this.updateEntrySelectionVisuals(prevEntryIndex);
        }

        FarmActions.setSlotItemPreview(this.activeSlot.slotID, this.currentlySelectedItemID);
    }

    private addEventListener(
        object: GameObject,
        event: GameObjectEvent,
        listener: (...args: unknown[]) => any,
        context?: any
    ): void {
        object.on(event, listener, context);

        this.cleanupManager.add(() => {
            object.off(event, listener);
        });
    }

    private getTabObjectFromLayout(index: number): GameObject {
        return ((this.layout as unknown) as { [key: string]: GameObject })['tabIcon_' + index];
    }

    private getTabNotificationObjectFromLayout(index: number): GameObject {
        return ((this.layout as unknown) as { [key: string]: GameObject })['notification_' + index];
    }

    public async preload() {
        return super.preload();
    }

    private configureTabs(): void {
        this.currentlyAvailableTabs = this.getTabs(this.activeSlot.slotID);
        this.currentTab = null;
        this.tabIcons = [];

        for (let i = 0; i < 6; i++) {
            this.tabIcons.push(this.getTabObjectFromLayout(i));
            if (this.currentlyAvailableTabs[i]) {
                this.tabIcons[i].getComponent(SpriteComponent).texture = this.currentlyAvailableTabs[i];
            }

            // Show all tabs.
            this.tabIcons[i].getComponent(SpriteComponent).visible = true;
        }

        // Hide inactive tabs.
        for (let i = this.currentlyAvailableTabs.length; i < 6; i++) {
            this.tabIcons[i].getComponent(SpriteComponent).visible = false;
        }

        this.tabIcons.forEach((_button, _indice) => {
            this.addEventListener(_button, GameObjectEvent.SELECTED, () => {
                FarmActions.setFarmCatalogueTab(this.currentlyAvailableTabs[_indice]);
            });
        });

        // Change tab based on selected item.
        const initialTab = this.currentlySelectedItemID
            ? getTabByItemId(this.currentlySelectedItemID)
            : this.currentlyAvailableTabs[0];

        FarmActions.setFarmCatalogueTab(initialTab);
    }

    private changeTab(_indice: number): void {
        if (this.currentTab === _indice) return;

        this.currentTab = _indice;
        this.layout.activeTab.container.x = this._tabStartingPositionX + 75 * _indice;

        const { type: slotType } = parseFarmSlotId(this.activeSlot.slotID);

        const state = Game.store.getState();
        this.itemsAvailableInTab = FarmStateHelper.sortFarmItems(
            FarmStateHelper.filterUnlockedFarmItems(
                state,
                FarmStateHelper.getFarmItemIdsForTab(this.currentlyAvailableTabs[this.currentTab], slotType),
                true
            )
        );

        this.setupEntries();

        this.layout.activeTab.container.x = this._tabStartingPositionX + SPACE_BETWEEN_TABS * _indice;
    }

    private clearActiveSlot() {
        this.activeSlot.oldParent.addChild(this.activeSlot);
        this.activeSlot = null;
    }

    private setActiveSlot(slotId: FarmSlotId): void {
        const { type: slotType } = parseFarmSlotId(slotId);
        this.activeSlot = FarmMapHandler.getSlot(slotId);
        this.isActiveSlotRemovable = slotType !== FarmSlotType.HOUSE;

        this.activeSlot.oldParent = this.activeSlot.parent;
        this.system.overlayPopup.parent.addChild(this.activeSlot);

        // Move highlight to active slot position.
        this.highlightSprite.container.position.copy(this.activeSlot.container.position);
    }

    private offsetPopup(): void {
        //  Offset popup relative to slot position
        // First get so midpoint is at center of screen
        const farmSlotGlobalPos = this.activeSlot.container.getGlobalPosition();
        const center = -(this.layout.bg.container.width / 2);
        const screenCenter = PIXIRenderer.renderer.width / 2;
        this.layout.bg.container.x = center + (farmSlotGlobalPos.x >= screenCenter ? -1 : 1) * 320;
        this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.layout.root.parent);
    }

    private selectSlot(slotId: FarmSlotId): void {
        // If a previous slot was selected: set the old slot to its old parent at its old z-index.
        if (this.activeSlot && slotId !== this.activeSlot.slotID) {
            this.clearActiveSlot();
        }
        this.setActiveSlot(slotId);
        this.offsetPopup();
        this.currentlySelectedItemID = this.activeSlot.itemId;
        this.configureTabs();
    }

    public onShow(params: OnShowParams) {
        super.onShow(params);
        const state = Game.store.getState();
        Game.ticker.addListener(TickerEvent.TICK, this.update, this);

        this.selectSlot(params.slotId);

        const isFTUXCompleted = FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM);
        this.layout.closeButton.getComponent(Interactable).enabled = isFTUXCompleted;
        if (!isFTUXCompleted) {
            this.layout.closeButton.getComponent(SpriteComponent).visible = false;
        }

        this.addEventListener(this.layout.closeButton, GameObjectEvent.SELECTED, this.onClose, this);
        this.addEventListener(this.layout.confirmButton, GameObjectEvent.SELECTED, this.onConfirm, this);
        // Clicking on the overlay behind the item selection UI confirms the changes
        this.addEventListener(this.system.overlayPopup, GameObjectEvent.SELECTED, this.onConfirm, this);
    }

    private onClose() {
        this.clearActiveSlot();
        this.highlightSprite.destroy();

        FarmActions.cancelSlotItem();
    }

    private onConfirm() {
        // Check `this.activeSlot` for `null` to suppress console errors if rapidly selecting `this.system.overlayPopup` to close menu.
        if (this.activeSlot === null) return;

        const { type } = parseFarmSlotId(this.activeSlot.slotID);

        const confirmAudio = !this.currentlySelectedItemID
            ? 'confirm_empty_slot_any'
            : type === FarmSlotType.BIG || FarmSlotType.SMALL
            ? 'confirm_slot_decoration'
            : type === FarmSlotType.CROP
            ? `confirm_slot_${FarmSlotType.CROP}`
            : `confirm_slot_${type}`;

        PlaySound(confirmAudio);

        FarmActions.confirmSlotItem(this.activeSlot.slotID, this.currentlySelectedItemID);

        this.system.scene.emit('NextFarmFTUX');

        this.clearActiveSlot();
        this.highlightSprite.destroy();
    }

    protected onHide(params?: any) {
        super.onHide(params);

        Game.ticker.removeListener(TickerEvent.TICK, this.update, this);
    }

    public handleStateChanged(state: IGameState): void {
        if (state.UIState.popupID !== PopupID.FARM_CATALOGUE) return;

        if (this.active && this.activeSlot?.slotID !== state.UIState.params.slotId) {
            this.selectSlot(state.UIState.params.slotId);
        }

        // Update tab selector.
        this.activeTabSelector(state);

        const { type: slotType } = parseFarmSlotId(state.UIState.params.slotId);

        // Update individual tab notification icons.
        this.tabIcons.forEach((_button, _indice) => {
            const tabItems = FarmStateHelper.filterUnlockedFarmItems(
                state,
                FarmStateHelper.getFarmItemIdsForTab(this.currentlyAvailableTabs[_indice], slotType)
            );
            const showUnseenNotification = FarmStateHelper.areAnyItemsUnseen(tabItems, state.playerState.decoItemsSeen);
            const notification = this.getTabNotificationObjectFromLayout(_indice);
            notification.getComponent(SpriteComponent).visible = showUnseenNotification;
        });
    }

    private setupEntries(): void {
        const requiredRows = Math.ceil(
            (this.itemsAvailableInTab.length + (this.isActiveSlotRemovable ? 1 : 0)) / SCROLL_ENTRY_TOTAL_COL
        );

        // Content height of scrollbox is pre-calculated based on number of rows and a fixed height. Bottom padding is added after.
        const scrollHeight = requiredRows * SCROLL_ENTRY_WIDTH_HEIGHT + SCROLL_ENTRY_Y_OFFSET;

        // Fixed width.
        const boxWidth = 475;

        // The box-height (window/mask height) is dependant on the number of visible rows to prevent minor offsets between the scroll-height and box-height.
        const boxHeight = (SCROLL_ENTRY_TOTAL_ROW - 1) * SCROLL_ENTRY_WIDTH_HEIGHT + SCROLL_ENTRY_Y_OFFSET;

        this.createScrollBox(boxWidth, boxHeight, boxWidth, scrollHeight);

        this.updateEntries();
    }

    private updateEntries() {
        const scrollTop = this.scroller.container.scrollTop;
        const rowsScrolled = Math.floor(scrollTop / SCROLL_ENTRY_WIDTH_HEIGHT);

        // Reset selection indicator visibility.
        this.layout.selectedItem.getComponent(SpriteComponent).visible = false;

        const state = Game.store.getState();

        for (let index = 0; index < this.entries.length; index++) {
            this.configureEntry(state, this.entries[index], index, rowsScrolled);
        }

        // Loop again to disable selection visuals. Not optimal but other methods are too complex.
        // This must happen after `configureEntry` is called for all entries to ensure `itemId` property is updated.
        for (let index = 0; index < this.entries.length; index++) {
            this.updateEntrySelectionVisuals(index);
        }
    }

    private configureEntry(state: IGameState, entry: IEntry, index: number, rowsScrolled: number): void {
        // Calculate row and column.
        const row = Math.floor(index / SCROLL_ENTRY_TOTAL_COL);
        const col = index - row * SCROLL_ENTRY_TOTAL_COL;

        // Calculate Y-offset for items based on scroll position, current row, and total number of visible rows.
        const page = Math.ceil((rowsScrolled - row) / SCROLL_ENTRY_TOTAL_ROW);
        const scrollOffset = page * SCROLL_ENTRY_TOTAL_ROW * SCROLL_ENTRY_WIDTH_HEIGHT;

        // Set position of each entry. Entries are recycled by adding a Y-offset and re-calculated the index.
        entry.root.container.x = SCROLL_ENTRY_X_OFFSET + col * SCROLL_ENTRY_WIDTH_HEIGHT;
        entry.root.container.y = SCROLL_ENTRY_Y_OFFSET + row * SCROLL_ENTRY_WIDTH_HEIGHT + scrollOffset;

        // Calculate the new index to access the correct catalog item.
        const calculatedIndex = index + page * SCROLL_ENTRY_TOTAL_ROW * SCROLL_ENTRY_TOTAL_COL;

        // Ignore negative entries (list does not loop).
        if (calculatedIndex < 0) return;

        // If the calculated entry index did not change there is no need to update the visuals of entry properties.
        if (entry.calculatedIndex === calculatedIndex) return;

        const catalogIndex = calculatedIndex - Number(this.isActiveSlotRemovable);
        entry.calculatedIndex = calculatedIndex;

        const entryIsOverflow = catalogIndex > this.itemsAvailableInTab.length - 1;
        entry.bg.getComponent(Interactable).enabled = !entryIsOverflow;
        entry.root.getComponent(Renderable).alpha = entryIsOverflow ? 0 : 1;

        if (entryIsOverflow) return;

        const isRemoveEntry = this.isActiveSlotRemovable && calculatedIndex === 0;

        if (isRemoveEntry) {
            entry.itemId = null;
            entry.isLoading = false;
            this.setEntryTexture(entry, 'myfarm_shop_ui_grass'); // myfarm_shop_ui_remove

            entry.xSprite = GameObject.Instantiate(Sprite, {
                texture: 'myfarm_shop_ui_removex',
                anchor: {
                    x: -0.1,
                    y: -0.1,
                },
                x: -(136 / 2),
                y: -(136 / 2),
            });
            entry.bg.addChild(entry.xSprite);
            entry.lockedTextBG.getComponent(SpriteComponent).visible = false;

            return;
        }

        if (entry.xSprite && !entry.xSprite.destroyed) {
            entry.xSprite.destroy();
            entry.xSprite = null;
        }

        entry.itemId = this.itemsAvailableInTab[catalogIndex];

        this.loadEntryTexture(state, entry);
    }

    private setEntryTexture(entry: IEntry, textureId: string) {
        const thumbnailSprite = entry.thumbnail.getComponent(SpriteComponent);
        const thumbnailTransform = entry.thumbnail.getComponent(TransformComponent);

        thumbnailSprite.texture = textureId;

        StopAnimation(entry.thumbnail);
        thumbnailTransform.rotation = 0;

        // TODO: Is this necessary?
        thumbnailTransform.scale.set(1, 1);
    }

    private loadEntryTexture(state: IGameState, entry: IEntry) {
        const textureId = getFarmItemThumbnailAssetName(entry.itemId);
        // Store index to validate after async texture loading. If it changed that means the entry was recycled and should not update.
        const currentIndex = entry.calculatedIndex;

        if (Game.resource.existsAndLoaded(textureId)) {
            this.setEntryTexture(entry, textureId);
            entry.isLoading = false;
            setNextEntry(state, entry);
            return;
        }

        // Do not reset loading if recycled entry is already loading.
        if (!entry.isLoading) {
            // Set loading spinner texture.
            this.setEntryTexture(entry, 'loading_farm_icon');
            entry.lockedTextBG.getComponent(SpriteComponent).visible = false;
            entry.isLoading = true;

            // Animate spinner.
            Anim(entry.thumbnail, {
                duration: 700,
                angle: 360,
                delay: 0,
                repeatCount: Infinity,
            });
        }

        if (!Game.resource.existsOrQueued(textureId)) {
            Game.resource.add(textureId, getFarmItemThumbnailPath(entry.itemId));
        }

        Game.resource.wait(textureId).then(() => {
            if (entry.thumbnail.destroyed) return;

            // Entry was recycled - ignore promise resolution.
            if (entry.calculatedIndex !== currentIndex) return;

            this.setEntryTexture(entry, textureId);
            entry.isLoading = false;

            setNextEntry(state, entry);
        });
    }

    private updateEntrySelectionVisuals(entryIndex: number): void {
        const entry = this.entries[entryIndex];

        // Ignore unused/invisible entries.
        const catalogIndex = entry.calculatedIndex - Number(this.isActiveSlotRemovable);
        const entryIsOverflow = catalogIndex > this.itemsAvailableInTab.length - 1;
        if (entryIsOverflow) return;

        if (entry.itemId === this.currentlySelectedItemID) {
            // Add selection indicator as child.
            entry.root.addChild(this.layout.selectedItem);

            // Show selection indicator.
            this.layout.selectedItem.getComponent(SpriteComponent).visible = true;

            // Hide X icon.
            if (entry.xSprite) entry.xSprite.getComponent(SpriteComponent).alpha = 0;
        } else {
            // Show X icon.
            if (entry.xSprite) entry.xSprite.getComponent(SpriteComponent).alpha = 1;
        }

        // Re-set relative positioning after add-child.
        this.layout.selectedItem.getComponent(TransformComponent).position.set(-8, -8);
    }

    public onDestroy() {
        super.onDestroy();
        this.entries = [];
        this.cleanupManager.cleanUpAll();
    }

    /**
     * Determine what tabs should be displayed based on unlocked items.
     * @param slotId
     * @private
     */
    private getTabs(slotId: FarmSlotId): FarmCatalogueTab[] {
        const { type: slotType } = parseFarmSlotId(slotId);

        const state = Game.store.getState();
        const items = FarmStateHelper.getAvailableItemsBySlotType(slotType);
        const filteredItems = FarmStateHelper.filterUnlockedFarmItems(state, items, true);

        // Use `Set` to ensure unique values.
        return (
            [...new Set(filteredItems.map(getTabByItemId))]
                // Sort to ensure tab order is correct.
                .sort((a, b) => {
                    return FarmCatalogueTabOrder[a] - FarmCatalogueTabOrder[b];
                })
        );
    }

    private update() {
        const deltaTime = Game.ticker.getDeltaTime();
        this.scroller?.container.content.update(deltaTime);
    }
}

/**
 * Get the correct tab for the specified item. Premium items always appear in the 'premium' tab.
 * @param itemId
 */
function getTabByItemId(itemId: FarmItemId) {
    return getFarmItemByID(itemId).tab;
}

function setNextEntry(state: IGameState, entry: IEntry) {
    const isNextItem = FarmStateHelper.isNextItem(state, entry.itemId);

    entry.bg.getComponent(Interactable).enabled = !isNextItem;
    entry.thumbnail.getComponent(ColorFilter).saturation = isNextItem ? 0 : 1;
    entry.lockedTextBG.getComponent(SpriteComponent).visible = isNextItem;

    if (isNextItem) {
        const unlockLevelNumber = FarmStateHelper.getItemLevelUnlock(state, entry.itemId);
        entry.lockedText.getComponent(TextComponent).text = Game.locale
            .getText('Level')
            .replace('[x]', unlockLevelNumber.toString());
    }
}
